import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/public-notices/PublicNoticesAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { PublicNoticesFilter } from "./public-notices-filter/PublicNoticesFilter";
import { PublicNoticesTable } from "./public-notices-table/PublicNoticesTable";
import { usePublicNoticesUIContext } from "./PublicNoticesUIContext";

export function PublicNoticesCard() {
  // PublicNotices UI Context
  const publicNoticesUIContext = usePublicNoticesUIContext();
  const publicNoticesUIProps = useMemo(() => {
    return {
      ids: publicNoticesUIContext.ids,
      setIds: publicNoticesUIContext.setIds,
      queryParams: publicNoticesUIContext.queryParams,
      openNewPublicNoticePage: publicNoticesUIContext.openNewPublicNoticePage,
    };
  }, [publicNoticesUIContext]);
    
  // Getting curret state of Public Notice list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.publicNotices }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // PublicNotices Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Genel Duyuru Listesini Excele Aktarır */
  const exportPublicNoticesToExcel = () => {
    dispatch(actions.getAllPublicNotices(publicNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 60, 60, 20, 30, 15];
      const columns = ["Gönderen", "Tanımı", "Açıklaması", "Tarih", "Ek", "Durum"];
      entities.forEach(entity => rows.push([entity.sender, entity.definition, entity.explanation, entity.createDate, entity.attachmentName, entity.isSent ? "Gönderildi" : "Beklemede"]));
      const filename = publicNoticesUIProps.queryParams.term + " - " + (publicNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Genel Duyuru Listesi";
      const sheetname = "Genel Duyuru Listesi"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Genel Duyuru Listesini PDF e Aktarır */
  const exportPublicNoticesToPDF = () => {
    dispatch(actions.getAllPublicNotices(publicNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Gönderen",
          rows: entities.map(entity => entity.sender)
        },
        {
          header: "Tanımı",
          rows: entities.map(entity => entity.definition)
        },
        {
          header: "Açıklaması",
          rows: entities.map(entity => entity.explanation)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.createDate)
        },
        {
          header: "Ek",
          rows: entities.map(entity => entity.attachmentName)
        },
        {
          header: "Durum",
          rows: entities.map(entity => entity.isSent ? "Gönderildi" : "Beklemede")
        }
      ];
  
      const filename = publicNoticesUIProps.queryParams.term + " - " + (publicNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Genel Duyuru Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Genel Duyuru Listesini Yazdırma Penceresini Açar */
  const printPublicNotices = () => {
    dispatch(actions.getAllPublicNotices(publicNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];
      const properties = ["Gönderen", "Tanımı", "Açıklaması", "Tarih", "Ek", "Durum"];
      const header = publicNoticesUIProps.queryParams.term + " - " + (publicNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Genel Duyuru Listesi";

      entities.forEach(entity => {
        data.push({
          "Gönderen": entity.sender,
          "Tanımı": entity.definition,
          "Açıklaması": entity.explanation,
          "Tarih": entity.createDate,
          "Ek": entity.attachmentName ?? "",
          "Durum": entity.isSent ? "Gönderildi" : "Beklemede"
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Genel Duyuru Listesini Panoya Kopyalar */
  const copyPublicNotices = () => {
    dispatch(actions.getAllPublicNotices(publicNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Gönderen", "Tanımı", "Açıklaması", "Tarih", "Ek", "Durum"];
      const header = publicNoticesUIProps.queryParams.term + " - " + (publicNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Genel Duyuru Listesi";
      entities.forEach(entity => rows.push([entity.sender, entity.definition, entity.explanation, entity.createDate, entity.attachmentName ?? "", entity.isSent ? "Gönderildi" : "Beklemede"]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Genel Duyuru (Okul Duyurusu) Listesini Siler */
  const deletePublicNotices = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deletePublicNotices(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);

    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        publicNoticesUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getPublicNotices(publicNoticesUIProps.queryParams));        
      }, 2500);
    }
  };
  
  const title = <>Genel Duyuru Listesi{publicNoticesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{publicNoticesUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportPublicNoticesToExcel}
                exportToPDF={exportPublicNoticesToPDF}
                print={printPublicNotices}
                copy={copyPublicNotices}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Duyuru"
                method={publicNoticesUIProps.openNewPublicNoticePage}
              />
            }
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deletePublicNotices(publicNoticesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <PublicNoticesFilter />
        <PublicNoticesTable
          deletePublicNotices={deletePublicNotices}
        />
      </CardBody>
    </Card>
  );
}